import React, { useCallback, useMemo } from "react";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "./DeviceConfigForm.module.css";
import { DeviceConfig, DeviceCategory, ElectrodeState } from "../../../services/types/device.types";
import {
  DEVICE_CATEGORIES,
  DEVICE_PANEL_CONFIG,
  ELECTRODE_SITES,
  WAVE_BANDS,
  applyPolarityElectrodeClick,
  cycleElectrodeState,
  getDefaultDeviceConfig,
  getPolarityElectrodeSummary,
  normalizeDeviceConfig,
} from "../../../utils/deviceConfiguration";

type DeviceConfigUpdater = DeviceConfig | ((prev: DeviceConfig) => DeviceConfig);

interface DeviceConfigFormProps {
  value: DeviceConfig;
  onChange: (update: DeviceConfigUpdater) => void;
  requireSessionWaves?: boolean;
  /** When set, category is fixed and the type selector is hidden (appointment session). */
  fixedCategory?: DeviceCategory;
}

function applyConfigUpdate(
  prev: DeviceConfig,
  update: DeviceConfigUpdater,
  fixedCategory?: DeviceCategory,
): DeviceConfig {
  const next = typeof update === "function" ? update(prev) : update;
  return normalizeDeviceConfig(fixedCategory ? { ...next, category: fixedCategory } : next);
}

function DeviceConfigForm({ value, onChange, requireSessionWaves = false, fixedCategory }: DeviceConfigFormProps) {
  const config = useMemo(
    () => normalizeDeviceConfig(fixedCategory ? { ...value, category: fixedCategory } : value),
    [value, fixedCategory],
  );
  const category = fixedCategory ?? config.category ?? "nf";
  const panel = DEVICE_PANEL_CONFIG[category];
  const polaritySummary = useMemo(
    () => (panel.electrodeMode === "polarity" ? getPolarityElectrodeSummary(config.electrodes) : null),
    [config.electrodes, panel.electrodeMode],
  );

  const updateConfig = useCallback(
    (update: DeviceConfigUpdater) => {
      onChange((prev) => applyConfigUpdate(prev, update, fixedCategory));
    },
    [fixedCategory, onChange],
  );

  const handleCategoryChange = useCallback(
    (next: DeviceCategory) => {
      updateConfig(getDefaultDeviceConfig(next));
    },
    [updateConfig],
  );

  const handleElectrodeClick = useCallback(
    (site: string) => {
      updateConfig((prev) => {
        const normalized = normalizeDeviceConfig(
          fixedCategory ? { ...prev, category: fixedCategory } : prev,
        );
        const electrodes =
          panel.electrodeMode === "polarity"
            ? applyPolarityElectrodeClick(normalized.electrodes ?? {}, site)
            : {
                ...normalized.electrodes,
                [site]: cycleElectrodeState(
                  (normalized.electrodes?.[site] ?? "unused") as ElectrodeState,
                  panel.electrodeMode,
                ),
              };

        return { ...normalized, electrodes };
      });
    },
    [fixedCategory, panel.electrodeMode, updateConfig],
  );

  const handleWaveChange = useCallback(
    (band: string, patch: Partial<{ enabled: boolean; direction: string; threshold?: number }>) => {
      updateConfig((prev) => {
        const normalized = normalizeDeviceConfig(
          fixedCategory ? { ...prev, category: fixedCategory } : prev,
        );
        return {
          ...normalized,
          waves: (normalized.waves ?? []).map((w) =>
            w.band === band ? { ...w, ...patch } : w,
          ),
        };
      });
    },
    [fixedCategory, updateConfig],
  );

  return (
    <div className={styles.form}>
      {!fixedCategory && (
        <div className={styles.field}>
          <label className={styles.label}>{t("DEVICE_TYPE")}</label>
          <select
            className={styles.select}
            value={category}
            onChange={(e) => handleCategoryChange(e.target.value as DeviceCategory)}
          >
            {DEVICE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {t(c.labelKey)}
              </option>
            ))}
          </select>
        </div>
      )}

      {panel.electrodes && (
        <div className={styles.field}>
          <label className={styles.label}>
            {panel.electrodeMode === "polarity"
              ? t("DEVICE_ELECTRODES_POLARITY")
              : t("DEVICE_ELECTRODES_ACTIVE")}
          </label>
          <div className={styles.electrodeGrid}>
            {ELECTRODE_SITES.map((site) => {
              const state = (config.electrodes?.[site] ?? "unused") as ElectrodeState;
              return (
                <button
                  key={site}
                  type="button"
                  className={`${styles.electrodeChip} ${styles[`electrode_${state}`]}`}
                  onClick={() => handleElectrodeClick(site)}
                >
                  {site}
                </button>
              );
            })}
          </div>
          <div className={styles.legend}>
            {panel.electrodeMode === "polarity" ? (
              <>
                <span><i className={`${styles.legendDot} ${styles.legend_anode}`} />{t("DEVICE_ANODE")}</span>
                <span><i className={`${styles.legendDot} ${styles.legend_cathode}`} />{t("DEVICE_CATHODE")}</span>
              </>
            ) : (
              <span><i className={`${styles.legendDot} ${styles.legend_active}`} />{t("DEVICE_ACTIVE")}</span>
            )}
            <span><i className={`${styles.legendDot} ${styles.legend_unused}`} />{t("DEVICE_UNUSED")}</span>
          </div>
          {polaritySummary && (
            <p className={styles.polarityHint}>
              {t("DEVICE_POLARITY_HINT")}
              {" "}
              {polaritySummary.anodeSites.length > 0
                ? `${t("DEVICE_ANODE")}: ${polaritySummary.anodeSites.join(", ")}`
                : `${t("DEVICE_ANODE")}: —`}
              {" · "}
              {polaritySummary.cathodeSites.length > 0
                ? `${t("DEVICE_CATHODE")}: ${polaritySummary.cathodeSites.join(", ")}`
                : `${t("DEVICE_CATHODE")}: —`}
            </p>
          )}
        </div>
      )}

      {panel.waves && (
        <div className={styles.field}>
          <label className={styles.label}>
            {t("DEVICE_WAVES")}
            {requireSessionWaves && <span className={styles.req}>*</span>}
          </label>
          <div className={styles.waveList}>
            {(config.waves ?? []).map((wave) => {
              const bandMeta = WAVE_BANDS.find((b) => b.key === wave.band);
              return (
                <div
                  key={wave.band}
                  className={`${styles.waveRow} ${!wave.enabled ? styles.waveRow_disabled : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={wave.enabled}
                    onChange={(e) =>
                      handleWaveChange(wave.band!, { enabled: e.target.checked })
                    }
                  />
                  <span className={styles.waveName}>
                    {bandMeta ? t(bandMeta.labelKey) : wave.band}
                  </span>
                  <select
                    className={styles.waveSelect}
                    disabled={!wave.enabled}
                    value={wave.direction ?? "up"}
                    onChange={(e) =>
                      handleWaveChange(wave.band!, { direction: e.target.value })
                    }
                  >
                    <option value="up">{t("DEVICE_WAVE_UP")}</option>
                    <option value="down">{t("DEVICE_WAVE_DOWN")}</option>
                  </select>
                  <div className={styles.threshold}>
                    <input
                      type="number"
                      disabled={!wave.enabled}
                      placeholder={t("DEVICE_THRESHOLD")}
                      value={wave.threshold ?? ""}
                      onChange={(e) =>
                        handleWaveChange(wave.band!, {
                          threshold: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                    />
                    <span>µV</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {panel.gonogo && (
        <div className={styles.field}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={config.goNoGo?.enabled ?? false}
              onChange={(e) =>
                updateConfig((prev) => {
                  const normalized = normalizeDeviceConfig(
                    fixedCategory ? { ...prev, category: fixedCategory } : prev,
                  );
                  return {
                    ...normalized,
                    goNoGo: { ...normalized.goNoGo, enabled: e.target.checked },
                  };
                })
              }
            />
            {t("DEVICE_GONOGO")}
          </label>
          <div
            className={`${styles.gonogoRow} ${
              !config.goNoGo?.enabled ? styles.gonogoRow_disabled : ""
            }`}
          >
            <div>
              <label>{t("DEVICE_GO_COUNT")}</label>
              <input
                type="number"
                min={0}
                disabled={!config.goNoGo?.enabled}
                value={config.goNoGo?.goCount ?? 20}
                onChange={(e) =>
                  updateConfig((prev) => {
                    const normalized = normalizeDeviceConfig(
                      fixedCategory ? { ...prev, category: fixedCategory } : prev,
                    );
                    return {
                      ...normalized,
                      goNoGo: {
                        ...normalized.goNoGo,
                        enabled: normalized.goNoGo?.enabled ?? false,
                        goCount: Number(e.target.value),
                      },
                    };
                  })
                }
              />
            </div>
            <div>
              <label>{t("DEVICE_STOP_COUNT")}</label>
              <input
                type="number"
                min={0}
                disabled={!config.goNoGo?.enabled}
                value={config.goNoGo?.stopCount ?? 10}
                onChange={(e) =>
                  updateConfig((prev) => {
                    const normalized = normalizeDeviceConfig(
                      fixedCategory ? { ...prev, category: fixedCategory } : prev,
                    );
                    return {
                      ...normalized,
                      goNoGo: {
                        ...normalized.goNoGo,
                        enabled: normalized.goNoGo?.enabled ?? false,
                        stopCount: Number(e.target.value),
                      },
                    };
                  })
                }
              />
            </div>
          </div>
        </div>
      )}

      <div className={styles.field}>
        <label className={styles.label}>
          {t("DEVICE_DURATION_MINUTES")} <span className={styles.req}>*</span>
        </label>
        <input
          type="number"
          min={1}
          className={styles.input}
          value={config.durationMinutes ?? 20}
          onChange={(e) =>
            updateConfig((prev) => {
              const normalized = normalizeDeviceConfig(
                fixedCategory ? { ...prev, category: fixedCategory } : prev,
              );
              return {
                ...normalized,
                durationMinutes: Number(e.target.value) || undefined,
              };
            })
          }
        />
      </div>
    </div>
  );
}

export default DeviceConfigForm;
